import React, { useMemo, useState } from 'react';
import { Button, CircularProgress, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../modules/store/hooks';
import { marketingPersonApi } from '../../../modules/services/marketingPersonService';
import { downloadPayoutExcel } from '../../../common/downloadMarketingPersonsExcel';

const PayoutsListScreen: React.FC = () => {
  const navigate = useNavigate();
  const language = useAppSelector(state => state.auth.language);
  const langCode = language?.split('-')?.[0] || 'en';

  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const filter = useMemo(() => ({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    search: search || undefined,
  }), [dateFrom, dateTo, search]);

  const { data, isFetching, refetch } = marketingPersonApi.useListPayoutsQuery(filter);
  const [deletePayout] = marketingPersonApi.useDeletePayoutMutation();

  const rows = useMemo(() => data || [], [data]);
  const totals = useMemo(() => {
    const totalAmount = rows.reduce((s, p) => s + Number(p.totalAmount || 0), 0);
    const totalEvent = rows.reduce((s, p) => s + Number(p.totalEventCommission || 0), 0);
    const totalReferral = rows.reduce((s, p) => s + Number(p.totalReferralCommission || 0), 0);
    return { totalAmount, totalEvent, totalReferral, count: rows.length };
  }, [rows]);

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deletePayout({ payoutId: confirmDeleteId, language: langCode }).unwrap();
      setConfirmDeleteId(null);
      refetch();
    } catch (e) {
      console.error('Failed to delete payout', e);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 60 },
    {
      field: 'createDate',
      headerName: 'Created',
      flex: 0.8, minWidth: 110,
      renderCell: (params: any) => <span>{params.row.createDate ? new Date(params.row.createDate).toLocaleDateString() : '-'}</span>,
    },
    {
      field: 'period',
      headerName: 'Period',
      flex: 1.2, minWidth: 180,
      renderCell: (params: any) => (
        <span>
          {params.row.dateFrom ? new Date(params.row.dateFrom).toLocaleDateString() : '-'}
          {' → '}
          {params.row.dateTo ? new Date(params.row.dateTo).toLocaleDateString() : '-'}
        </span>
      ),
    },
    { field: 'marketingPersonName', headerName: 'Marketing Person', flex: 1.2, minWidth: 140 },
    { field: 'eventItemsCount', headerName: 'Events', flex: 0.5, minWidth: 70 },
    { field: 'referralItemsCount', headerName: 'Referrals', flex: 0.6, minWidth: 80 },
    {
      field: 'totalEventCommission',
      headerName: 'Event Commission',
      flex: 0.9, minWidth: 110,
      renderCell: (params: any) => <span>₪{Number(params.row.totalEventCommission || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalReferralCommission',
      headerName: 'Referral',
      flex: 0.7, minWidth: 100,
      renderCell: (params: any) => <span>₪{Number(params.row.totalReferralCommission || 0).toLocaleString()}</span>,
    },
    {
      field: 'totalAmount',
      headerName: 'Total',
      flex: 0.7, minWidth: 100,
      renderCell: (params: any) => <Typography variant="body2" color="success.main" fontWeight="bold">₪{Number(params.row.totalAmount || 0).toLocaleString()}</Typography>,
    },
    { field: 'createdByPhone', headerName: 'By', flex: 0.9, minWidth: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.9, minWidth: 130,
      sortable: false,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <IconButton size="small" color="warning" onClick={() => navigate(`/marketing-persons/payouts/${params.row.id}`)}>
            <Tooltip title="Open"><span style={{ fontSize: 18 }}>↗</span></Tooltip>
          </IconButton>
          <IconButton size="small" color="warning" onClick={() => downloadPayoutExcel(params.row.id)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setConfirmDeleteId(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div style={{ height: 'auto', width: '100%', position: 'relative' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Tabs
          value={1}
          onChange={(_, v) => {
            if (v === 0) navigate('/marketing-persons/all');
            if (v === 2) navigate('/marketing-persons/payments');
          }}
          sx={{ mt: 1, mb: 2, ml: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
          TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
        >
          <Tab label="Marketing persons" />
          <Tab label="Payouts" />
          <Tab label="Legacy payments" />
        </Tabs>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap', padding: '0 10px' }}>
          <TextField
            size="small"
            color="warning"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <DatePicker
            label="From"
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(v: Dayjs | null) => setDateFrom(v ? v.format('YYYY-MM-DD') : '')}
            renderInput={(params) => <TextField {...params} size="small" color="warning" />}
          />
          <DatePicker
            label="To"
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(v: Dayjs | null) => setDateTo(v ? v.format('YYYY-MM-DD') : '')}
            renderInput={(params) => <TextField {...params} size="small" color="warning" />}
          />
          {(search || dateFrom || dateTo) && (
            <Tooltip title="Clear filters">
              <IconButton size="small" color="warning" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}>
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div style={{ margin: '0 10px 16px', padding: 16, border: '1px solid #fc7f03', borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <StatCard label="Payouts" value={String(totals.count)} />
          <StatCard label="Event commission" value={`₪${totals.totalEvent.toLocaleString()}`} />
          <StatCard label="Referral commission" value={`₪${totals.totalReferral.toLocaleString()}`} />
          <StatCard label="Total paid out" value={`₪${totals.totalAmount.toLocaleString()}`} strong />
        </div>

        {isFetching ? (
          <CircularProgress color="warning" />
        ) : (
          <DataGrid
            sx={{
              backgroundColor: '#ede9e4',
              border: 'none',
              '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', color: '#fff' },
              '.MuiDataGrid-cell': { border: '1px solid #fc7f03' },
            }}
            rows={rows}
            columns={columns}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            density="comfortable"
          />
        )}

        {confirmDeleteId !== null && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}
            onClick={() => setConfirmDeleteId(null)}
          >
            <div style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 320 }} onClick={(e) => e.stopPropagation()}>
              <Typography variant="h6" color="warning.main">Delete payout?</Typography>
              <Typography sx={{ my: 2 }}>
                This will remove the payout, unfreezing the event/referral commissions it contained.
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button color="warning" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
              </div>
            </div>
          </div>
        )}
      </LocalizationProvider>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div style={{ padding: '12px 20px', borderRadius: 8, backgroundColor: '#ede9e4', textAlign: 'center' }}>
    <Typography variant="body2" color="textSecondary">{label}</Typography>
    <Typography variant="h6" color={strong ? 'success.main' : 'warning.main'} fontWeight={strong ? 'bold' : 500}>{value}</Typography>
  </div>
);

export default PayoutsListScreen;
